import { useNavigate, useSearch } from "@tanstack/react-router";
import { TableState, TableUrlParameters } from "components/UI/Table/types/table.ts";
import { TableId } from "types/models.ts";

import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from "../atoms/tableAtom";

import PageRequest = App.Http.Requests.PageRequest;
import FilterRequest = App.Http.Requests.FilterRequest;

interface SearchParams {
    tableId: TableId;
    [key: string]: unknown;
}

// Utility to flatten an object to dot notation, with flat filter keys
function flattenObjectToDotNotation(obj: any, prefix = ""): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (key === "filter" && typeof value === "object" && value !== null && value.filter) {
            // Flatten filter fields directly under the prefix (skip 'filter' and 'filter.filter')
            const filterBlock = value.filter;
            for (const fieldName in filterBlock) {
                if (!Object.prototype.hasOwnProperty.call(filterBlock, fieldName)) continue;
                const opObj = filterBlock[fieldName];
                if (typeof opObj === "object" && opObj !== null && !Array.isArray(opObj)) {
                    for (const operator in opObj) {
                        if (!Object.prototype.hasOwnProperty.call(opObj, operator)) continue;
                        result[`${prefix ? prefix + "." : ""}${fieldName}.${operator}`] = opObj[operator];
                    }
                }
            }
        } else if (key === "page" && typeof value === "object" && value !== null) {
            // Flatten page fields as t32.page.number, t32.page.size
            Object.assign(result, flattenObjectToDotNotation(value, newKey));
        } else if (key === "sort" && value !== undefined) {
            // Handle sort parameter at the top level
            result[newKey] = value;
        } else if (value && typeof value === "object" && !Array.isArray(value)) {
            Object.assign(result, flattenObjectToDotNotation(value, newKey));
        } else {
            result[newKey] = value;
        }
    }
    return result;
}

// Utility to unflatten dot notation keys to an object, with flat filter keys
function unflattenDotNotationToObject(flatObj: Record<string, any>, prefix: string): any {
    const result: any = {};
    for (const flatKey in flatObj) {
        if (!flatKey.startsWith(prefix + ".")) continue;
        const rest = flatKey.slice(prefix.length + 1); // remove prefix and dot
        if (rest.startsWith("page.")) {
            // Pagination
            const pageField = rest.slice(5); // after 'page.'
            result.page = result.page || {};
            result.page[pageField] = flatObj[flatKey];
        } else if (rest === "sort") {
            // Sort parameter
            result.sort = flatObj[flatKey];
        } else {
            // Filter: split at last dot
            const lastDot = rest.lastIndexOf(".");
            if (lastDot === -1) continue;
            const fieldName = rest.slice(0, lastDot);
            const operator = rest.slice(lastDot + 1);
            result.filter = result.filter || {};
            result.filter.filter = result.filter.filter || {};
            result.filter.filter[fieldName] = result.filter.filter[fieldName] || {};
            result.filter.filter[fieldName][operator] = flatObj[flatKey];
        }
    }
    return result;
}

export const useTableUrlParameters = <T extends object>(tableId: TableId) => {
    const navigate = useNavigate();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    // Convert searchParams (URLSearchParams) to a plain object
    const getSearchParamsObject = () => {
        return { ...searchParams };
    };

    const getUrlParameters = (): TableUrlParameters | null => {
        // Collect all params that start with tableId.
        const paramsObj = getSearchParamsObject();
        const tableParams = unflattenDotNotationToObject(paramsObj, tableId);
        // If nothing found, return null
        if (Object.keys(tableParams).length === 0) return null;
        return tableParams;
    };

    const isDefaultFilter = (filter: FilterRequest | undefined) => !filter || Object.keys(filter).length === 0;

    const isDefaultParams = (params: TableUrlParameters) => isDefaultFilter(params.filter) && params.sort === undefined;

    const setUrlParameters = (parameters: TableUrlParameters | null) => {
        const paramsObj = getSearchParamsObject();

        // Remove all previous tableId.* params
        Object.keys(paramsObj).forEach((key) => {
            if (key === tableId || key.startsWith(`${tableId}.`)) {
                delete paramsObj[key];
            }
        });
        // Flatten new parameters
        let flatParams: Record<string, any> = {};
        if (parameters) {
            // Always include page parameters, BUT only if not default
            if (parameters.page) {
                const { number, size, ...restPage } = parameters.page;
                const isDefaultNumber = number === DEFAULT_PAGE_NUMBER;
                const isDefaultSize = size === DEFAULT_PAGE_SIZE;
                const pageToFlatten: any = {};
                if (!isDefaultNumber) pageToFlatten.number = number;
                if (!isDefaultSize) pageToFlatten.size = size;

                Object.assign(pageToFlatten, restPage);
                if (Object.keys(pageToFlatten).length > 0) {
                    const pageParams = flattenObjectToDotNotation({ page: pageToFlatten }, tableId);
                    flatParams = { ...flatParams, ...pageParams };
                }
            }

            // Include other parameters only if they're not default
            if (!isDefaultParams(parameters)) {
                const otherParams = flattenObjectToDotNotation(
                    {
                        ...parameters,
                        page: undefined, // Remove page to avoid duplication
                    },
                    tableId
                );
                flatParams = { ...flatParams, ...otherParams };
            }
        }
        // Merge with other search params
        const newSearch = { ...paramsObj, ...flatParams };
        navigate({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - navigate search
            search: newSearch,
            replace: true,
        });
    };

    const setDefaultUrlParameters = (): TableUrlParameters => {
        const defaultParameters: TableUrlParameters = {
            page: {
                number: DEFAULT_PAGE_NUMBER,
                size: DEFAULT_PAGE_SIZE,
            },
        };
        setUrlParameters(defaultParameters);
        return defaultParameters;
    };

    const getTableStateWithUrlParameters = (tableState: TableState<T>): TableState<T> => {
        const urlParameters = getUrlParameters();
        if (!urlParameters) {
            const defaultParameters = setDefaultUrlParameters();
            tableState = {
                ...tableState,
                page: defaultParameters.page as PageRequest,
                filter: defaultParameters?.filter as FilterRequest,
            };
        } else {
            // Create a new filter object with the sort parameter if it exists
            let newFilter = (urlParameters?.filter as FilterRequest) || {};
            if (urlParameters.sort) {
                newFilter = { ...newFilter, sort: urlParameters.sort };
            }

            tableState = {
                ...tableState,
                page: urlParameters.page as PageRequest,
                filter: newFilter,
            };
        }
        return tableState;
    };

    const removeFilters = (): void => {
        const paramsObj = getSearchParamsObject();
        // Preserve the sort parameter if it exists
        const sortParam = paramsObj[`${tableId}.sort`];

        // Remove all filter keys for this tableId
        Object.keys(paramsObj).forEach((key) => {
            if (key.startsWith(`${tableId}.filter`)) {
                delete paramsObj[key];
            }
        });

        // Make sure we keep the sort parameter
        if (sortParam !== undefined) {
            paramsObj[`${tableId}.sort`] = sortParam;
        }

        navigate({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - navigate search
            search: paramsObj,
            replace: true,
        });
    };

    return {
        getUrlParameters,
        setUrlParameters,
        getTableStateWithUrlParameters,
        removeFilters,
    };
};
